import React, { useState } from 'react';
import styled from 'styled-components';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Tooltip } from '@studio/legacy-components';
import { SINGLE_SIGN_ON, SINGLE_SIGN_ON_BETA } from 'next/entities/features';
import { asPromised } from 'utils/as-promised';

import { selectUser } from 'reducers/user';
import { selectAccountUsers } from 'reducers/account/users';

import { selectAccountFeature } from 'reducers/account/features';
import { selectAccountInfo } from 'reducers/account/meta';
import { updateSsoSetting } from 'actions/account/meta';

import { canManageAccountUsers } from 'helpers/account-management';
import { BeaconLink } from 'components/Common/HelpScout';

import InvitePermissionModal from './InvitePermissionModal';

const CTAButton = styled(Button)`
  background: var(--disabled);
  color: var(--white);

  box-shadow: none;
  transition: background 0ms;

  &:hover {
    box-shadow: var(--button-elevation);
  }
`;

export const Settings = ({
  disabled,
  hasSSO,
  hasSSOBeta,
  discoverable,
  onUpdate,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleCLose = () => {
    setShowModal(false);
    setShowError(false);
  };

  const handleOpen = () => {
    setShowModal(true);
  };

  const handleSave = async value => {
    try {
      await onUpdate(value);
      handleCLose();
    } catch {
      setShowError(true);
    }
  };

  if (!hasSSOBeta) {
    return null;
  }

  return (
    <>
      {hasSSO && !disabled && (
        <Button role="button" kind="secondary" onClick={handleOpen}>
          SSO configuration
        </Button>
      )}

      {!hasSSO && (
        <Tooltip
          label={
            <>
              <BeaconLink
                subject="Interested in SSO"
                message="I'd like to upgrade my plan to include SSO."
                light
              >
                Upgrade
              </BeaconLink>{' '}
              your plan for SSO.{' '}
            </>
          }
          persist
        >
          <CTAButton data-ctt="Clicked disabled SSO configuration button">
            SSO configuration
          </CTAButton>
        </Tooltip>
      )}

      <InvitePermissionModal
        discoverable={discoverable}
        visible={showModal}
        error={showError}
        onClose={handleCLose}
        onSave={handleSave}
      />
    </>
  );
};

Settings.propTypes = {
  disabled: PropTypes.bool,
  hasSSO: PropTypes.bool,
  hasSSOBeta: PropTypes.bool,
  discoverable: PropTypes.bool,

  onUpdate: PropTypes.func,
};

const mapStateToProps = state => {
  const user = selectUser(state);
  const accountUsers = selectAccountUsers(state);
  const accountInfo = selectAccountInfo(state);

  return {
    discoverable: accountInfo.sso_discoverability_enabled,
    disabled: !canManageAccountUsers({ user, accountUsers }),
    hasSSO: selectAccountFeature(state, SINGLE_SIGN_ON),
    hasSSOBeta: selectAccountFeature(state, SINGLE_SIGN_ON_BETA),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdate: value =>
    asPromised(
      dispatch,
      updateSsoSetting({
        account: {
          sso_discoverability_enabled: value,
        },
      })
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
